import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    type ReactNode,
} from 'react';
import { useActionButtonCallbacks } from '@splunk/dashboard-context';

export const ActionMenuPopup = ({
    children,
    onRequestClose,
}: {
    children: ReactNode;
    onRequestClose: () => void;
}) => {
    const { onMouseEnter, onMouseLeave, onVisibilityChange } =
        useActionButtonCallbacks();

    // Abstract the callbacks to a stable ref so the cloned child remains valid
    const onRequestCloseRef = useRef(onRequestClose);
    onRequestCloseRef.current = onRequestClose;

    const onMouseLeaveRef = useRef(onMouseLeave);
    onMouseLeaveRef.current = onMouseLeave;

    const handleRequestClose = useCallback(() => {
        onRequestCloseRef.current();
        onMouseLeaveRef.current?.(
            null as unknown as React.MouseEvent<HTMLElement>
        );
    }, []);

    // Register a link to handleRequestClose to be used when the menu visibility changes
    useEffect(() => {
        onVisibilityChange?.(handleRequestClose);
    }, [handleRequestClose, onVisibilityChange]);

    const child = useMemo(() => {
        if (
            React.Children.count(children) !== 1 ||
            !React.isValidElement(children) ||
            typeof onRequestClose !== 'function'
        ) {
            return children;
        }

        return React.cloneElement(children, {
            onRequestClose: handleRequestClose,
        });
    }, [children, handleRequestClose, onRequestClose]);

    return (
        <div
            role="none"
            data-test="action-menu-popup-wrapper"
            onMouseEnter={onMouseEnter}
        >
            {child}
        </div>
    );
};
